import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clotureVide, sortReferencesIntrouvables } from '../../server/services/compta/cycleExport';
import { comptaApi, type ResumeExport, type StatutExport } from '../services/compta-api';
import { Spinner } from '../components/shared/Spinner';
import { quandCompta } from '../lib/dates-compta';

/**
 * Exports comptables mensuels remis à KPMG.
 *
 * L'écran suit l'ordre du brief : générer, CONTRÔLER, déposer, puis marquer
 * transmis. Rien ne part sans l'utilisatrice, et tant que l'export n'est pas
 * transmis il peut être annulé — ce qui libère ses pièces.
 *
 * Ce qui est montré avant toute transmission n'est pas décoratif :
 *   - les pièces RATTRAPÉES, datées d'un mois antérieur ;
 *   - le journal des pièces ÉCARTÉES avec leur motif, seule preuve que rien de
 *     légitime n'a disparu ;
 *   - les pièces REFUSÉES, qui ne sont dans aucun fichier ;
 *   - les pièces déjà transmises qui ont CHANGÉ dans Books depuis leur envoi ;
 *   - les pièces dont la référence « Annule et remplace » est restée INTROUVABLE :
 *     elles partent, mais le cabinet ne saura pas quelle facture elles annulent,
 *     et aucune relecture ultérieure ne le rattrapera.
 *
 * La génération tourne en tâche de fond : l'écran interroge l'export tant qu'il
 * est `en_cours` (polling conditionnel, cf. CLAUDE.md).
 */

const STATUT_LIBELLE: Record<StatutExport, string> = {
  en_cours: 'En cours',
  genere: 'Généré, à contrôler',
  transmis: 'Transmis',
  annule: 'Annulé',
  echec: 'Échec',
};
const STATUT_BADGE: Record<StatutExport, string> = {
  en_cours: 'bg-blue-100 text-blue-800 ring-blue-200',
  genere: 'bg-amber-100 text-amber-900 ring-amber-200',
  transmis: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  annule: 'bg-slate-100 text-slate-600 ring-slate-200',
  echec: 'bg-red-100 text-red-800 ring-red-200',
};

const euros = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const moisLisible = (mois: string) => {
  const [a, m] = mois.split('-').map(Number);
  return new Date(Date.UTC(a!, m! - 1, 1)).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};
/** Le formateur vit dans `src/lib/dates-compta.ts` : deux écrans affichent le
 *  même champ, et ils doivent dire la même date. */
const quand = quandCompta;

/** Le mois précédent : celui qu'on clôture presque toujours. */
function moisParDefaut(): string {
  const d = new Date();
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function ComptaExportsPage() {
  const qc = useQueryClient();
  const [mois, setMois] = useState(moisParDefaut);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [motifAnnulation, setMotifAnnulation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ['compta', 'exports'],
    queryFn: comptaApi.liste,
    // Tant qu'une clôture tourne, l'écran se remet à jour tout seul.
    refetchInterval: (q) => (q.state.data?.exports.some((e) => e.statut === 'en_cours') ? 3000 : false),
  });

  const exports = liste.data?.exports ?? [];
  const actif = useMemo(() => exports.find((e) => e.id === ouvert) ?? exports.find((e) => e.statut === 'en_cours') ?? exports.find((e) => e.statut === 'genere') ?? null, [exports, ouvert]);

  // Les trois gestes partagent le même après-coup : effacer l'erreur et
  // relire la liste. Écrits à plat, parce qu'un hook ne s'appelle pas depuis
  // une fonction d'aide.
  const apresCoup = {
    onSuccess: () => {
      setErreur(null);
      void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
    },
    onError: (e: Error) => setErreur(e.message),
  };

  const generer = useMutation({
    mutationFn: () => comptaApi.generer(mois),
    onSuccess: (r) => {
      setErreur(null);
      setOuvert(r.id);
      void qc.invalidateQueries({ queryKey: ['compta', 'exports'] });
    },
    onError: (e: Error) => setErreur(e.message),
  });
  const deposer = useMutation({ mutationFn: () => comptaApi.deposer(actif!.id), ...apresCoup });
  const transmettre = useMutation({ mutationFn: () => comptaApi.transmettre(actif!.id), ...apresCoup });
  const annuler = useMutation({
    mutationFn: () => comptaApi.annuler(actif!.id, motifAnnulation),
    ...apresCoup,
    onSuccess: () => {
      setMotifAnnulation('');
      apresCoup.onSuccess();
    },
  });

  if (liste.isLoading) return <Spinner />;
  // Un échec de lecture N'EST PAS une absence de clôture : sans ce test, la
  // liste vide s'affichait avec « Aucune clôture générée », et un mois déjà
  // clôturé passait pour jamais fait.
  if (liste.isError)
    return (
      <div className="p-4 md:p-6">
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Exports comptables</h1>
        <p className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-800 ring-1 ring-red-200">
          Les clôtures n’ont pas pu être lues : {(liste.error as Error).message}. Rien n’a été modifié ; réessayez, et prévenez si cela persiste.
        </p>
      </div>
    );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header>
        <h1 className="font-[Sora] text-2xl font-bold text-[#384872]">Exports comptables</h1>
        <p className="text-sm text-slate-600">Les quatre rapports de pièces remis à KPMG, mois par mois.</p>
      </header>

      {/* OÙ IRONT LES DÉPÔTS, dit AVANT d'en faire un. Le chemin ne se lisait
          jusqu'ici qu'après coup, sur la clôture déposée : trop tard pour voir
          qu'un environnement de test visait le dossier réel de KPMG. */}
      {liste.data?.depot?.incoherent && (
        <p className="rounded-md bg-red-50 p-4 text-sm text-red-900 ring-2 ring-red-400">
          <strong>Attention : cet environnement est un environnement de TEST, mais les dépôts iraient dans le dossier réel de KPMG.</strong>
          <br />
          <span className="font-mono text-xs">{liste.data.depot.racine}</span>
          <br />
          La variable <span className="font-mono">DROPBOX_EXPORTS_ROOT</span> doit être posée sous <span className="font-mono">/_STAGING/</span>. Ne déposez rien avant de l’avoir corrigée.
        </p>
      )}
      {liste.data?.depot && !liste.data.depot.incoherent && (
        <p
          className={`rounded-md p-3 text-sm ring-1 ${
            liste.data.depot.dossierDeTest ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-slate-50 text-slate-700 ring-slate-200'
          }`}
        >
          {liste.data.depot.dossierDeTest ? (
            <>
              <strong>Dossier de TEST</strong> — les dépôts de cet environnement n’arrivent pas chez KPMG :
            </>
          ) : (
            <>Les dépôts arrivent dans le dossier consulté par KPMG :</>
          )}{' '}
          <span className="font-mono text-xs">{liste.data.depot.racine}</span>
        </p>
      )}

      {liste.data && !liste.data.registreAmorce && (
        <p className="rounded-md bg-amber-50 p-4 text-sm text-amber-900 ring-1 ring-amber-200">
          <strong>Le registre n’est pas encore amorcé.</strong> Une génération ferait remonter tout l’historique de Books. Passez d’abord par l’écran d’initialisation, après le dernier envoi fait à la main.
        </p>
      )}

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-700">Mois à clôturer</span>
            <input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2" />
          </label>
          <button
            type="button"
            onClick={() => generer.mutate()}
            // Le serveur refuse déjà une génération sans registre ; le bouton
            // ne doit pas l'inviter pour autant.
            disabled={generer.isPending || !liste.data?.registreAmorce}
            className="rounded-md bg-[#384872] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {generer.isPending ? 'Lancement…' : 'Générer'}
          </button>
          <span className="text-xs text-slate-500">La génération lit Books pièce par pièce : quelques minutes.</span>
        </div>
        {erreur && <p className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">{erreur}</p>}
      </section>

      {actif && (
        <DetailExport
          exp={actif}
          onDeposer={() => deposer.mutate()}
          onTransmettre={() => transmettre.mutate()}
          onAnnuler={() => annuler.mutate()}
          motif={motifAnnulation}
          setMotif={setMotifAnnulation}
          occupe={deposer.isPending || transmettre.isPending || annuler.isPending}
        />
      )}

      <section className="rounded-lg border border-slate-200 bg-white">
        <h2 className="border-b border-slate-200 px-4 py-3 font-[Sora] font-semibold text-[#384872]">Historique</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Mois</th>
              <th className="px-4 py-2">Statut</th>
              <th className="px-4 py-2">Pièces</th>
              <th className="px-4 py-2">Généré</th>
              <th className="px-4 py-2">Transmis</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {exports.map((e) => (
              <tr key={e.id} className="border-t border-slate-100">
                <td className="px-4 py-2 font-medium">{moisLisible(e.mois)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ring-1 ${STATUT_BADGE[e.statut]}`}>{STATUT_LIBELLE[e.statut]}</span>
                </td>
                <td className="px-4 py-2">{e.pieces}</td>
                <td className="px-4 py-2 text-slate-600">{quand(e.genere_at)}</td>
                <td className="px-4 py-2 text-slate-600">{quand(e.transmis_at)}</td>
                <td className="px-4 py-2 text-right">
                  <button type="button" onClick={() => setOuvert(e.id)} className="text-sm text-blue-700 hover:underline">
                    Voir
                  </button>
                </td>
              </tr>
            ))}
            {!exports.length && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Aucune clôture générée pour l’instant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/** Le dossier Dropbox où les classeurs sont partis, déduit de leurs chemins. */
function dossierDeDepot(fichiers: ResumeExport['fichiers']): string {
  const chemin = fichiers[0]?.chemin ?? '';
  return chemin.split('/').slice(0, -1).join('/');
}

function DetailExport({
  exp,
  onDeposer,
  onTransmettre,
  onAnnuler,
  motif,
  setMotif,
  occupe,
}: {
  exp: ResumeExport;
  onDeposer: () => void;
  onTransmettre: () => void;
  onAnnuler: () => void;
  motif: string;
  setMotif: (v: string) => void;
  occupe: boolean;
}) {
  const [annulationOuverte, setAnnulationOuverte] = useState(false);
  const rapports = Object.entries(exp.totaux?.rapports ?? {});
  // Une clôture VIDE : le mois n'avait aucune pièce à transmettre. La règle est
  // PARTAGÉE avec le serveur (`compta/cycleExport.ts`, module pur) pour que
  // l'écran et le service ne puissent pas en juger différemment.
  const vide = clotureVide(exp.totaux);
  // Une clôture transmise VIDE reste annulable : elle n'a réservé aucune pièce,
  // donc rien à libérer. C'est le seul moyen de retirer de l'historique une
  // transmission qui n'a rien transmis. La règle est tenue côté serveur
  // (`refusAnnulation`) ; l'écran ne fait que proposer le geste.
  const annulable = exp.statut === 'genere' || (exp.statut === 'transmis' && vide && exp.pieces === 0);
  const modifiees = exp.totaux?.modifiees ?? [];
  const refusees = exp.totaux?.refusees ?? [];
  const referencesIntrouvables = exp.totaux?.referencesIntrouvables ?? null;
  const sortReferences = sortReferencesIntrouvables(exp.statut);
  const rattrapees = rapports.flatMap(([, r]) => r.rattrapees.map((p) => ({ ...p, fichier: r.fichier })));

  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-[Sora] text-lg font-semibold text-[#384872]">
          Clôture de {moisLisible(exp.mois)} <span className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs ring-1 ${STATUT_BADGE[exp.statut]}`}>{STATUT_LIBELLE[exp.statut]}</span>
        </h2>
        <span className="text-xs text-slate-500">Générée par {exp.genere_par}, le {quand(exp.genere_at)}</span>
      </div>

      {exp.statut === 'en_cours' && (
        <p className="flex items-center gap-3 rounded-md bg-blue-50 p-3 text-sm text-blue-900 ring-1 ring-blue-200">
          <Spinner /> {exp.progression ?? 'En cours…'}
        </p>
      )}
      {exp.statut === 'echec' && <p className="rounded-md bg-red-50 p-3 text-sm text-red-800 ring-1 ring-red-200">Échec : {exp.erreur}</p>}
      {vide && exp.statut !== 'annule' && (
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <p className="font-medium">Clôture vide : aucune pièce à transmettre.</p>
          <p className="mt-1 text-xs">
            {exp.totaux?.dejaTransmises
              ? `${exp.totaux.dejaTransmises} pièce(s) datée(s) jusqu’à la fin du mois sont déjà au registre : elles sont donc déjà parties chez KPMG. Ce mois n’a rien de nouveau à transmettre.`
              : 'Aucune pièce n’était datée de ce mois ni d’un mois antérieur non transmis.'}
          </p>
          <p className="mt-1 text-xs">
            Les classeurs ne contiennent que leurs en-têtes : le dépôt et la transmission sont désactivés. Annulez cette clôture, elle n’a rien réservé.
          </p>
        </div>
      )}

      {exp.statut === 'annule' && (
        <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-700 ring-1 ring-slate-200">
          Annulé le {quand(exp.annule_at)} — {exp.annule_motif}. Ses pièces sont libérées : elles reviendront à la prochaine clôture.
        </p>
      )}

      {rapports.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="py-2">Fichier</th>
              <th className="py-2">Pièces</th>
              <th className="py-2">Lignes</th>
              <th className="py-2 text-right">Total TTC</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {rapports.map(([cle, r]) => (
              <tr key={cle} className="border-t border-slate-100">
                <td className="py-2">{r.fichier}</td>
                <td className="py-2">{r.pieces}</td>
                <td className="py-2">{r.lignes}</td>
                <td className="py-2 text-right tabular-nums">{euros(r.totalTtc)} €</td>
                <td className="py-2 text-right">
                  <a href={comptaApi.lienFichier(exp.id, r.fichier)} className="text-sm text-blue-700 hover:underline">
                    Télécharger
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* LES CINQ BLOCS SONT TOUJOURS LÀ, même vides, avec « aucune ».
          Un bloc qui disparaît quand il n'a rien à dire ne se distingue pas
          d'un bloc absent : impossible alors de savoir si le mois n'a aucune
          pièce refusée, ou si l'information manque.

          Ils n'apparaissent qu'une fois la génération TERMINÉE (les rapports
          existent) : afficher « aucune » pendant le traitement affirmerait un
          résultat qui n'est pas encore calculé. */}
      {rapports.length > 0 && (
      <>
      <Bloc titre={`Pièces refusées : ${refusees.length === 0 ? 'aucune' : refusees.length}`} ton={refusees.length ? 'rouge' : 'neutre'}>
        {refusees.length === 0 ? (
          <p className="text-xs">Toutes les pièces retenues ont pu être écrites dans les fichiers.</p>
        ) : (
          <>
            <p className="mb-2 text-xs">Ces pièces ne sont dans AUCUN fichier : à corriger dans Books, puis regénérer.</p>
            <ul className="space-y-1">
              {refusees.map((r) => (
                <li key={`${r.type}-${r.numero}`}>
                  <strong>{r.numero}</strong> ({r.type}) — {r.motif}
                </li>
              ))}
            </ul>
          </>
        )}
      </Bloc>

      {/* TOUJOURS LÀ, même à zéro, comme les blocs voisins : « aucune » est une
          information avant un envoi au cabinet.

          La référence n'entre pas dans l'empreinte : une fois la clôture
          transmise, plus rien ne la rattrapera. C'est ici, et seulement ici,
          qu'elle se voit.

          Une clôture générée AVANT l'ajout de la colonne n'a pas ce compte : ses
          fichiers n'ont pas la colonne, et dire « toutes portent leur numéro »
          serait faux. */}
      <Bloc
        titre={`Références « Annule et remplace » introuvables : ${
          referencesIntrouvables === null ? 'non vérifiées' : referencesIntrouvables.length === 0 ? 'aucune' : referencesIntrouvables.length
        }`}
        ton={referencesIntrouvables?.length ? 'ambre' : 'neutre'}
      >
        {referencesIntrouvables === null ? (
          <p className="text-xs">
            Cette clôture a été générée avant l’ajout de la colonne « Annule et remplace » : ses fichiers ne la contiennent pas.
          </p>
        ) : referencesIntrouvables.length === 0 ? (
          <p className="text-xs">
            Toutes les pièces qui annulent une facture portent bien son numéro. Le cabinet saura, pour chacune, laquelle elle remplace.
          </p>
        ) : (
          <>
            {/* Le texte suit le statut (`sortReferencesIntrouvables`) : proposer
                d'annuler une clôture transmise, ou dire « transmise » d'une
                clôture annulée, indiquerait un geste ou un état faux. */}
            {sortReferences === 'liberees' ? (
              <p className="text-xs">
                Ces pièces annulent une facture dont le numéro n’a pas pu être retrouvé dans Books. Cette clôture étant annulée, elles reviendront avec la
                prochaine.
              </p>
            ) : (
              <p className="text-xs">
                Ces pièces annulent une facture dont le numéro n’a pas pu être retrouvé dans Books.{' '}
                <strong>{sortReferences === 'parties' ? 'Elles sont parties avec cette clôture' : 'Elles partent bien avec cette clôture'}</strong> : rien
                n’a été bloqué. Mais leur colonne « Annule et remplace » est vide, et le cabinet ne saura donc pas quelle facture elles annulent.
              </p>
            )}
            <ul className="mt-2 space-y-1">
              {referencesIntrouvables.map((r, i) => (
                <li key={`${r.numero}-${i}`}>
                  <strong>{r.numero}</strong>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs">
              {sortReferences === 'a_corriger' &&
                'Pour y remédier : saisir à la main dans Books le numéro de la facture annulée — champ « Facture annulée liée » sur une facture rectificative, « Référence » sur un avoir —, puis annuler cette clôture et la regénérer.'}
              {sortReferences === 'parties' &&
                'Cette clôture est transmise : Gertrude ne peut plus compléter cette colonne pour elle. Signalez au cabinet, pour chacune, le numéro de la facture annulée.'}
              {sortReferences === 'liberees' &&
                'Pour que la prochaine clôture porte la référence : saisir à la main dans Books le numéro de la facture annulée — champ « Facture annulée liée » sur une facture rectificative, « Référence » sur un avoir — avant de la générer.'}
            </p>
          </>
        )}
      </Bloc>

      <Bloc
        titre={`Pièces déjà transmises, modifiées dans Books depuis : ${modifiees.length === 0 ? 'aucune' : modifiees.length}`}
        ton={modifiees.length ? 'ambre' : 'neutre'}
      >
        {modifiees.length === 0 ? (
          <p className="text-xs">Aucune pièce déjà partie chez KPMG n’a bougé depuis son envoi.</p>
        ) : (
          <>
            <p className="mb-2 text-xs">KPMG en a l’ancienne version. Aucun renvoi automatique : à traiter avec le cabinet.</p>
            <ul className="space-y-1">
              {modifiees.map((m) => (
                <li key={m.zohoId}>
                  <strong>{m.numero}</strong> ({m.type}) — {euros(m.montantRegistre)} € à l’envoi, {euros(m.montantActuel)} € aujourd’hui
                </li>
              ))}
            </ul>
          </>
        )}
      </Bloc>

      {/* Les RATTRAPAGES : le cœur de ce que le registre apporte. Sans cette
          section, il fallait ouvrir les classeurs pour savoir si le mois en
          contenait — soit revenir au travail manuel. */}
      <Bloc
        titre={`Pièces de mois antérieurs remontées automatiquement : ${rattrapees.length === 0 ? 'aucune' : rattrapees.length}`}
        ton={rattrapees.length ? 'bleu' : 'neutre'}
      >
        {rattrapees.length === 0 ? (
          <p className="text-xs">Aucune pièce d’un mois antérieur ne restait à transmettre.</p>
        ) : (
          <>
            <p className="text-xs">
              Ces pièces sont datées d’un mois antérieur et ne figuraient dans aucun export précédent : elles partent donc avec cette clôture.{' '}
              <strong>Aucune action de votre part n’est requise.</strong>
            </p>
            {/* REPLIABLE, fermé par défaut, comme le bloc des pièces écartées :
                un mois qui rattrape des dizaines de pièces déroulait sinon un
                tableau interminable au milieu du compte rendu. Le titre porte
                déjà le nombre, donc rien ne se cache. */}
            <details className="mt-2">
              <summary className="cursor-pointer text-xs">Voir ces {rattrapees.length} pièce(s), avec leur mois d’origine et leur montant</summary>
            <table className="mt-2 w-full text-sm">
              <thead className="text-left text-xs uppercase opacity-70">
                <tr>
                  <th className="py-1">Pièce</th>
                  <th className="py-1">Tiers</th>
                  <th className="py-1">Mois d’origine</th>
                  <th className="py-1 text-right">Montant TTC</th>
                  <th className="py-1">Rapport</th>
                </tr>
              </thead>
              <tbody>
                {rattrapees.map((p) => (
                  <tr key={`${p.fichier}-${p.numero}`} className="border-t border-black/5">
                    <td className="py-1 font-medium">{p.numero}</td>
                    <td className="py-1">{p.tiers}</td>
                    <td className="py-1">{moisLisible(p.date.slice(0, 7))}</td>
                    <td className="py-1 text-right tabular-nums">{euros(p.montant)} €</td>
                    <td className="py-1 text-xs opacity-70">{p.fichier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </details>
          </>
        )}
      </Bloc>

      <Bloc titre={`Pièces écartées : ${exp.exclusions.length === 0 ? 'aucune' : exp.exclusions.length}`} ton="neutre">
        {exp.exclusions.length === 0 ? (
          <p className="text-xs">Aucune pièce n’a été écartée du périmètre ce mois-ci.</p>
        ) : (
          <details>
            <summary className="cursor-pointer text-xs">Voir le détail et le motif de chacune</summary>
            <ul className="mt-2 space-y-1">
              {exp.exclusions.map((e, i) => (
                <li key={`${e.numero}-${i}`}>
                  <strong>{e.numero}</strong> — {e.tiers} — {e.motif}
                </li>
              ))}
            </ul>
          </details>
        )}
      </Bloc>
      </>
      )}

      {exp.fichiers.length > 0 && (
        <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-900 ring-1 ring-emerald-200">
          <p className="font-medium">{exp.fichiers.length} fichier(s) déposé(s) dans Dropbox</p>
          {/* Le DOSSIER en clair : c'est ce qui permet de vérifier, d'un coup
              d'œil, que le dépôt est allé au bon endroit — et pas dans un
              dossier de test. */}
          <p className="mt-1 break-all font-mono text-xs">{dossierDeDepot(exp.fichiers)}</p>
          <ul className="mt-1">
            {exp.fichiers.map((f) => (
              <li key={f.nom}>{f.nom}</li>
            ))}
          </ul>
        </div>
      )}

      {(exp.statut === 'genere' || exp.statut === 'transmis') && (
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
          {exp.statut === 'genere' && (
            <>
              {/* Une clôture vide ne se dépose pas et ne se transmet pas : il
                  n'y a que des en-têtes dans ses classeurs. */}
              <button type="button" onClick={onDeposer} disabled={occupe || vide} className="rounded-md bg-[#384872] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                Déposer dans Dropbox
              </button>
              <button type="button" onClick={onTransmettre} disabled={occupe || vide || !exp.fichiers.length} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                Marquer transmis
              </button>
              {/* Le motif n'apparaît qu'APRÈS le clic : un champ de saisie
                  visible en permanence donne à croire qu'il faut le remplir,
                  alors que l'annulation est l'exception. */}
              {!annulationOuverte ? (
                <button type="button" onClick={() => setAnnulationOuverte(true)} disabled={occupe} className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50">
                  Annuler l’export
                </button>
              ) : null}
            </>
          )}
          {exp.statut === 'transmis' && !vide && (
            <p className="text-sm text-emerald-800">Transmis le {quand(exp.transmis_at)} : les pièces sont définitivement au registre.</p>
          )}
          {exp.statut === 'transmis' && vide && !annulationOuverte && (
            <>
              <p className="text-sm text-amber-900">Transmise le {quand(exp.transmis_at)}, mais VIDE : elle n’a transmis aucune pièce.</p>
              <button type="button" onClick={() => setAnnulationOuverte(true)} disabled={occupe} className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 disabled:opacity-50">
                Annuler cette clôture vide
              </button>
            </>
          )}
        </div>
      )}

      {annulable && annulationOuverte && (
        <div className="space-y-2 rounded-md bg-red-50 p-3 text-sm text-red-900 ring-1 ring-red-200">
          <p className="font-medium">Annuler cette clôture ?</p>
          <p className="text-xs">
            {exp.pieces === 0
              ? 'Elle n’a réservé aucune pièce : l’annuler ne libère rien et ne change donc rien à ce que KPMG a reçu. Elle disparaîtra simplement de l’historique actif.'
              : `Ses ${exp.pieces} pièce(s) seront libérées et reviendront dans la prochaine clôture. Les fichiers déjà déposés dans Dropbox, eux, restent à supprimer à la main.`}
          </p>
          <label className="block">
            <span className="mb-1 block font-medium">Motif de l’annulation</span>
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-red-300 p-2 text-sm text-slate-900"
              placeholder="Ce qui a motivé l’annulation : il restera dans l’historique."
            />
          </label>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onAnnuler} disabled={occupe || motif.trim().length < 3} className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              Confirmer l’annulation
            </button>
            <button type="button" onClick={() => setAnnulationOuverte(false)} disabled={occupe} className="text-sm text-slate-600 hover:underline">
              Revenir en arrière
            </button>
            {motif.trim().length < 3 && <span className="text-xs">Le motif est obligatoire.</span>}
          </div>
        </div>
      )}
    </section>
  );
}

function Bloc({ titre, ton, children }: { titre: string; ton: 'rouge' | 'ambre' | 'bleu' | 'neutre'; children: React.ReactNode }) {
  // Le ton NEUTRE sert aux blocs vides : « aucune pièce refusée » est une bonne
  // nouvelle, elle ne doit pas s'afficher en rouge.
  const styles = {
    rouge: 'bg-red-50 text-red-900 ring-red-200',
    ambre: 'bg-amber-50 text-amber-900 ring-amber-200',
    bleu: 'bg-blue-50 text-blue-900 ring-blue-200',
    neutre: 'bg-slate-50 text-slate-700 ring-slate-200',
  }[ton];
  return (
    <div className={`rounded-md p-3 text-sm ring-1 ${styles}`}>
      <p className="mb-1 font-medium">{titre}</p>
      {children}
    </div>
  );
}
